import React, { useMemo } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
} from '@mui/material';
import DetailedCompetencyAnalysis from '../DetailedCompetencyAnalysis/index';

const KEY_COMPETENCY_THRESHOLD = 0.6;
const HIGH_ACTIVITY_THRESHOLD = 0.66;

const getFlowState = (challenge, skill) => {
  if (skill >= 0.66 && challenge >= 0.66) return 'FLOW';
  if (skill < 0.33 && challenge >= 0.66) return 'ANXIÉTÉ';
  if (skill >= 0.66 && challenge < 0.33) return 'ENNUI';
  if (skill < 0.33 && challenge < 0.33) return 'INDIFFÉRENCE';
  if (skill >= 0.33 && skill < 0.66 && challenge >= 0.66) return 'EXCITATION';
  if (skill >= 0.66 && challenge >= 0.33 && challenge < 0.66) return 'MAITRISE';
  if (skill >= 0.33 && skill < 0.66 && challenge < 0.33) return 'DETACHEMENT';
  if (skill < 0.33 && challenge >= 0.33 && challenge < 0.66) return 'INQUIETUDE';
  return 'NEUTRE';
};

const getTimeDiscrepancy = (current, desired) => {
  const gap = desired - current;
  if (gap > 0.33) return 'Très Insuffisamment';
  if (gap > 0.15) return 'Insuffisamment';
  if (gap < -0.33) return 'Beaucoup Trop';
  if (gap < -0.15) return 'Trop';
  return null;
};

const getCompetencyDiscrepancy = (timeScore, competencyScore) => {
  const gap = competencyScore - timeScore;
  if (gap > 0.33) return '<<';
  if (gap > 0.15) return '<';
  if (gap < -0.33) return '>>';
  if (gap < -0.15) return '>';
  return null;
};

const getDiscrepancySeverity = (timeDiscrepancy, competencyDiscrepancy) => {
  const timeScore = timeDiscrepancy === 'Très Insuffisamment' ? 4 :
                   timeDiscrepancy === 'Insuffisamment' ? 3 :
                   timeDiscrepancy === 'Beaucoup Trop' ? 4 :
                   timeDiscrepancy === 'Trop' ? 3 : 0;

  const compScore = competencyDiscrepancy === '<<' ? 4 :
                   competencyDiscrepancy === '<' ? 3 :
                   competencyDiscrepancy === '>>' ? 4 :
                   competencyDiscrepancy === '>' ? 3 : 0;

  return Math.max(timeScore, compScore);
};

const ActivitySynthesisTable = ({ results }) => {
  const synthesisData = useMemo(() => {
    if (!results?.questions) return null;

    const timeSpent = results.questions.find(q => q.questionKey === 'time_spent')?.rankings || [];
    const timeDesired = results.questions.find(q => q.questionKey === 'time_desired')?.rankings || [];
    const competencies = results.questions.find(q => q.questionKey === 'competency')?.rankings || [];

    // Group activities by competency level
    const groupedActivities = competencies
      .sort((a, b) => b.score - a.score)
      .reduce((acc, comp) => {
        const timeData = timeSpent.find(t => t.item === comp.item);
        const desiredData = timeDesired.find(d => d.item === comp.item);
        
        const competencyLevel = comp.score >= 0.66 ? 'élevées' :
                              comp.score >= 0.33 ? 'moyennes' : 'faibles';
        
        const frequencyLevel = timeData.score >= 0.66 ? 'élevée' :
                             timeData.score >= 0.33 ? 'modérée' : 'faible';

        const flowState = getFlowState(timeData.score, comp.score);
        
        // Calculate discrepancies
        const timeDiscrepancy = getTimeDiscrepancy(timeData.score, desiredData.score);
        const competencyDiscrepancy = getCompetencyDiscrepancy(timeData.score, comp.score);
        
        // More inclusive threshold for key competencies (0.6 instead of 0.5)
        const isKeyCompetency = comp.score >= KEY_COMPETENCY_THRESHOLD && timeData.score >= KEY_COMPETENCY_THRESHOLD;

        if (!acc[competencyLevel]) {
          acc[competencyLevel] = [];
        }

        acc[competencyLevel].push({
          activity: comp.item,
          frequency: frequencyLevel,
          flowState,
          competencyScore: comp.score,
          timeScore: timeData.score,
          isKeyCompetency,
          timeDiscrepancy,
          competencyDiscrepancy
        });

        return acc;
      }, {});

    // Add severity score to each activity but don't filter
    Object.keys(groupedActivities).forEach(level => {
      const activities = groupedActivities[level];
      
      groupedActivities[level] = activities.map(activity => ({
        ...activity,
        severityScore: getDiscrepancySeverity(activity.timeDiscrepancy, activity.competencyDiscrepancy)
      }));
    });

    return groupedActivities;
  }, [results]);

  if (!synthesisData) return null;

  const stateColors = {
    FLOW: '#2e7d32',         // Dark green
    ANXIÉTÉ: '#c62828',      // Dark red
    ENNUI: '#1565c0',        // Dark blue
    INDIFFÉRENCE: '#757575', // Dark grey
    EXCITATION: '#d84315',   // Dark orange
    MAITRISE: '#388e3c',     // Medium green
    DETACHEMENT: '#0d47a1',  // Dark blue
    INQUIETUDE: '#e65100',   // Dark orange
    NEUTRE: '#616161'        // Medium grey
  };

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell style={{ width: '40%' }}>Liste des activités</TableCell>
              <TableCell style={{ width: '20%' }}>Fréquence élevée</TableCell>
              <TableCell style={{ width: '20%' }}>Fréquence modérée</TableCell>
              <TableCell style={{ width: '20%' }}>Fréquence faible</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Compétences élevées */}
            <TableRow>
              <TableCell colSpan={4} style={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #e0e0e0' }}>
                <Typography variant="subtitle1" style={{ fontWeight: 500, color: '#424242' }}>
                  Compétences élevées
                </Typography>
              </TableCell>
            </TableRow>
            {synthesisData.élevées?.map((item, index) => (
              <TableRow key={`high-${index}`}>
                <TableCell>
                  <Typography>
                    <Box component="span" sx={{ 
                      fontWeight: item.isKeyCompetency ? 'bold' : 'normal',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}>
                      {item.activity}
                      {item.isKeyCompetency && (
                        <Typography
                          component="span"
                          sx={{
                            ml: 1,
                            color: '#fff',
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            bgcolor: '#1976d2',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            opacity: 0.9
                          }}
                        >
                          Compétence-clé
                        </Typography>
                      )}
                    </Box>
                  </Typography>
                </TableCell>
                <TableCell>
                  {item.frequency === 'élevée' && (
                    <Box>
                      <Typography style={{ color: stateColors[item.flowState] }}>
                        {item.flowState}
                      </Typography>
                      {(item.timeDiscrepancy || item.competencyDiscrepancy) && (
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 1, 
                          alignItems: 'center',
                          mt: 0.5,
                          flexWrap: 'wrap'
                        }}>
                          {item.timeDiscrepancy && (
                            <Typography
                              component="span"
                              sx={{
                                color: 'error.main',
                                fontSize: '0.75rem'
                              }}
                            >
                              {item.timeDiscrepancy}
                            </Typography>
                          )}
                          {item.competencyDiscrepancy && (
                            <Typography
                              component="span"
                              sx={{
                                color: 'warning.main',
                                fontSize: '0.75rem'
                              }}
                            >
                              {item.competencyDiscrepancy}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  {item.frequency === 'modérée' && (
                    <Box>
                      <Typography style={{ color: stateColors[item.flowState] }}>
                        {item.flowState}
                      </Typography>
                      {(item.timeDiscrepancy || item.competencyDiscrepancy) && (
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 1, 
                          alignItems: 'center',
                          mt: 0.5,
                          flexWrap: 'wrap'
                        }}>
                          {item.timeDiscrepancy && (
                            <Typography
                              component="span"
                              sx={{
                                color: 'error.main',
                                fontSize: '0.75rem'
                              }}
                            >
                              {item.timeDiscrepancy}
                            </Typography>
                          )}
                          {item.competencyDiscrepancy && (
                            <Typography
                              component="span"
                              sx={{
                                color: 'warning.main',
                                fontSize: '0.75rem'
                              }}
                            >
                              {item.competencyDiscrepancy}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  {item.frequency === 'faible' && (
                    <Box>
                      <Typography style={{ color: stateColors[item.flowState] }}>
                        {item.flowState}
                      </Typography>
                      {(item.timeDiscrepancy || item.competencyDiscrepancy) && (
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 1, 
                          alignItems: 'center',
                          mt: 0.5,
                          flexWrap: 'wrap'
                        }}>
                          {item.timeDiscrepancy && (
                            <Typography
                              component="span"
                              sx={{
                                color: 'error.main',
                                fontSize: '0.75rem'
                              }}
                            >
                              {item.timeDiscrepancy}
                            </Typography>
                          )}
                          {item.competencyDiscrepancy && (
                            <Typography
                              component="span"
                              sx={{
                                color: 'warning.main',
                                fontSize: '0.75rem'
                              }}
                            >
                              {item.competencyDiscrepancy}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ))}

            {/* Compétences moyennes */}
            <TableRow>
              <TableCell colSpan={4} style={{ backgroundColor: '#f5f5f5' }}>
                <Typography variant="subtitle1">Compétences moyennes</Typography>
              </TableCell>
            </TableRow>
            {synthesisData.moyennes?.map((item, index) => (
              <TableRow key={`medium-${index}`}>
                <TableCell>
                  <Typography>
                    <Box component="span" sx={{ 
                      fontWeight: item.isKeyCompetency ? 'bold' : 'normal',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}>
                      {item.activity}
                      {item.isKeyCompetency && (
                        <Typography
                          component="span"
                          sx={{
                            ml: 1,
                            color: '#fff',
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            bgcolor: '#1976d2',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            opacity: 0.9
                          }}
                        >
                          Compétence-clé
                        </Typography>
                      )}
                    </Box>
                  </Typography>
                </TableCell>
                <TableCell>
                  {item.frequency === 'élevée' && (
                    <Box>
                      <Typography style={{ color: stateColors[item.flowState] }}>
                        {item.flowState}
                      </Typography>
                      {(item.timeDiscrepancy || item.competencyDiscrepancy) && (
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 1, 
                          alignItems: 'center',
                          mt: 0.5,
                          flexWrap: 'wrap'
                        }}>
                          {item.timeDiscrepancy && (
                            <Typography
                              component="span"
                              sx={{
                                color: 'error.main',
                                fontSize: '0.75rem'
                              }}
                            >
                              {item.timeDiscrepancy}
                            </Typography>
                          )}
                          {item.competencyDiscrepancy && (
                            <Typography
                              component="span"
                              sx={{
                                color: 'warning.main',
                                fontSize: '0.75rem'
                              }}
                            >
                              {item.competencyDiscrepancy}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  {item.frequency === 'modérée' && (
                    <Box>
                      <Typography style={{ color: stateColors[item.flowState] }}>
                        {item.flowState}
                      </Typography>
                      {(item.timeDiscrepancy || item.competencyDiscrepancy) && (
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 1, 
                          alignItems: 'center',
                          mt: 0.5,
                          flexWrap: 'wrap'
                        }}>
                          {item.timeDiscrepancy && (
                            <Typography
                              component="span"
                              sx={{
                                color: 'error.main',
                                fontSize: '0.75rem'
                              }}
                            >
                              {item.timeDiscrepancy}
                            </Typography>
                          )}
                          {item.competencyDiscrepancy && (
                            <Typography
                              component="span"
                              sx={{
                                color: 'warning.main',
                                fontSize: '0.75rem'
                              }}
                            >
                              {item.competencyDiscrepancy}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  {item.frequency === 'faible' && (
                    <Box>
                      <Typography style={{ color: stateColors[item.flowState] }}>
                        {item.flowState}
                      </Typography>
                      {(item.timeDiscrepancy || item.competencyDiscrepancy) && (
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 1, 
                          alignItems: 'center',
                          mt: 0.5,
                          flexWrap: 'wrap'
                        }}>
                          {item.timeDiscrepancy && (
                            <Typography
                              component="span"
                              sx={{
                                color: 'error.main',
                                fontSize: '0.75rem'
                              }}
                            >
                              {item.timeDiscrepancy}
                            </Typography>
                          )}
                          {item.competencyDiscrepancy && (
                            <Typography
                              component="span"
                              sx={{
                                color: 'warning.main',
                                fontSize: '0.75rem'
                              }}
                            >
                              {item.competencyDiscrepancy}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ))}

            {/* Compétences faibles */}
            <TableRow>
              <TableCell colSpan={4} style={{ backgroundColor: '#f5f5f5' }}>
                <Typography variant="subtitle1">Compétences faibles</Typography>
              </TableCell>
            </TableRow>
            {synthesisData.faibles?.map((item, index) => (
              <TableRow key={`low-${index}`}>
                <TableCell>
                  <Typography>
                    <Box component="span" sx={{ 
                      fontWeight: item.isKeyCompetency ? 'bold' : 'normal',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}>
                      {item.activity}
                      {item.isKeyCompetency && (
                        <Typography
                          component="span"
                          sx={{
                            ml: 1,
                            color: '#fff',
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            bgcolor: '#1976d2',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            opacity: 0.9
                          }}
                        >
                          Compétence-clé
                        </Typography>
                      )}
                    </Box>
                  </Typography>
                </TableCell>
                <TableCell>
                  {item.frequency === 'élevée' && (
                    <Box>
                      <Typography style={{ color: stateColors[item.flowState] }}>
                        {item.flowState}
                      </Typography>
                      {(item.timeDiscrepancy || item.competencyDiscrepancy) && (
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 1, 
                          alignItems: 'center',
                          mt: 0.5,
                          flexWrap: 'wrap'
                        }}>
                          {item.timeDiscrepancy && (
                            <Typography
                              component="span"
                              sx={{
                                color: 'error.main',
                                fontSize: '0.75rem'
                              }}
                            >
                              {item.timeDiscrepancy}
                            </Typography>
                          )}
                          {item.competencyDiscrepancy && (
                            <Typography
                              component="span"
                              sx={{
                                color: 'warning.main',
                                fontSize: '0.75rem'
                              }}
                            >
                              {item.competencyDiscrepancy}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  {item.frequency === 'modérée' && (
                    <Box>
                      <Typography style={{ color: stateColors[item.flowState] }}>
                        {item.flowState}
                      </Typography>
                      {(item.timeDiscrepancy || item.competencyDiscrepancy) && (
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 1, 
                          alignItems: 'center',
                          mt: 0.5,
                          flexWrap: 'wrap'
                        }}>
                          {item.timeDiscrepancy && (
                            <Typography
                              component="span"
                              sx={{
                                color: 'error.main',
                                fontSize: '0.75rem'
                              }}
                            >
                              {item.timeDiscrepancy}
                            </Typography>
                          )}
                          {item.competencyDiscrepancy && (
                            <Typography
                              component="span"
                              sx={{
                                color: 'warning.main',
                                fontSize: '0.75rem'
                              }}
                            >
                              {item.competencyDiscrepancy}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  {item.frequency === 'faible' && (
                    <Box>
                      <Typography style={{ color: stateColors[item.flowState] }}>
                        {item.flowState}
                      </Typography>
                      {(item.timeDiscrepancy || item.competencyDiscrepancy) && (
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 1, 
                          alignItems: 'center',
                          mt: 0.5,
                          flexWrap: 'wrap'
                        }}>
                          {item.timeDiscrepancy && (
                            <Typography
                              component="span"
                              sx={{
                                color: 'error.main',
                                fontSize: '0.75rem'
                              }}
                            >
                              {item.timeDiscrepancy}
                            </Typography>
                          )}
                          {item.competencyDiscrepancy && (
                            <Typography
                              component="span"
                              sx={{
                                color: 'warning.main',
                                fontSize: '0.75rem'
                              }}
                            >
                              {item.competencyDiscrepancy}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ))}

            <TableRow>
              <TableCell colSpan={4} sx={{ borderTop: '2px solid #e0e0e0', py: 2 }}>
                <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                  Légende:
                </Typography>
                <Typography variant="caption" display="block" color="error.main">
                  Très Insuffisamment/Insuffisamment/Trop/Beaucoup Trop: temps consacré par rapport au temps souhaité
                </Typography>
                <Typography variant="caption" display="block" color="warning.main">
                  &lt;&lt;/&lt;/&gt;/&gt;&gt;: temps consacré par rapport au niveau de compétence
                </Typography>
                <Typography variant="caption" display="block">
                  Les activités en gras sont celles où vous avez un intérêt élevé et/ou un niveau de compétence élevé
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <DetailedCompetencyAnalysis synthesisData={synthesisData} />
    </>
  );
};

export default ActivitySynthesisTable;
